import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Image, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SmallCards from "../../components/dashboard/SmallCards";
import commission from "../../assets/images/commission.webp";
import network from "../../assets/images/network.webp";
import personal from "../../assets/images/personal.webp";
import pool from "../../assets/images/pool.webp";
import {
  endRowTemplate,
  formatNumberToLocal,
  formatProductionMonthLabel,
  selectColourStyles2,
  sortProductionMonthaData,
} from "../../helpers";
import IncomeDOC from "../../components/dashboard/IncomeDOC";
import ListingCard from "../../components/ListingCard";
import TeamPerformance from "../../components/teamPerformance/TeamPerformance";
import {
  useGetProductionMonthListQuery,
  useNewCollabDashboardQuery,
  useNewExCollabDashboardQuery,
} from "../../app/features/apiSlice";
import SetPageTitle from "../../components/SetPageTitle";
import { useParams } from "react-router";
import moment from "moment";
import Performer from "../../components/teamPerformance/Performer";
import { Link } from "react-router-dom";
import KycCardDashboard from "../../components/kyc/KycCardDashboard";
import SmallCards2 from "../../components/dashboard/SmallCards2";
import DashboardChart from "../../components/dashboard/DashboardChart";
import ProfileLoader from "../../components/dashboard/ProfileLoader";
import { selectUserData } from "../../app/features/DataSlice";
import { useSelector } from "react-redux";
import { SkSmallCard } from "../../components/Skeleton";
import EwalletCard from "../../components/dashboard/EwalletCard";
import { isWithinInterval, parseISO } from "date-fns";
import Select from "react-select";
import { skipToken } from "@reduxjs/toolkit/query";
import CurrencyValue from "../../components/CurrencyValue";

const CollaboratorDashboard = () => {
  const userData = useSelector(selectUserData);
  // eslint-disable-next-line no-unused-vars
  const [hasInvestment, setHasInvestment] = useState(false);
  const { t } = useTranslation();
  const sessionId = useRef(Date.now()).current;
  const { lang } = useParams();
  const [date, setDate] = useState({});
  const [options, setOptions] = useState([]);
  const [selOption, setSelOption] = useState({});

  const filter = date.month ? { sessionId, lang, date } : skipToken;

  const {
    data,
    isFetching: isLoading,
    isError,
    error,
    isSuccess,
  } = useNewCollabDashboardQuery(filter);
  const productionMonth = useGetProductionMonthListQuery({ sessionId });
  const exceliorData = useNewExCollabDashboardQuery({ sessionId, lang });

  // Derive endRowData based on the fetched data
  const endRowData = React.useMemo(() => {
    if (!isSuccess) return endRowTemplate;
    const { data: itemData } = data;
    return endRowTemplate.map((section) => {
      if (
        section.title === "discountCoupons" &&
        itemData?.givenDiscounts.length > 0
      ) {
        return {
          ...section,
          items: section.items.map((item) =>
            item.title === "toBeUsed"
              ? { ...item, value: data?.data?.givenDiscounts.join(", ") }
              : item,
          ),
        };
      }
      if (section.title === "activityStatus") {
        const activityStatus = itemData?.activityStatus;
        return {
          ...section,
          items: section.items
            // Hide the row entirely when there's no value coming from the API.
            .filter((item) => {
              if (item.title !== "rightToOvers") return true;
              return (
                activityStatus?.rightsToOverUntil !== null &&
                activityStatus?.rightsToOverUntil !== undefined &&
                activityStatus?.rightsToOverUntil !== ""
              );
            })
            .map((item) => {
              if (item.title === "academyRegistration") {
                return {
                  ...item,
                  status: activityStatus?.academicRegistration
                    ? "active"
                    : "inactive",
                };
              }
              if (item.title === "presenceAtWebinars") {
                return {
                  ...item,
                  status: activityStatus?.presenceAtWebinar
                    ? "active"
                    : "inactive",
                };
              }
              if (item.title === "rightToOvers") {
                return {
                  ...item,
                  value: activityStatus?.rightsToOverUntil,
                };
              }
              if (item.title === "heliorPlay") {
                return {
                  ...item,
                  status: activityStatus?.isHeliorPlay ? "active" : "inactive",
                };
              }
              return item;
            }),
        };
      }
      return section;
    });
  }, [isSuccess, data]);

  const handleSetDate = (dateString) => {
    if (dateString) {
      const [month, year] = dateString.split(" ");
      setSelOption({
        value: `${month} ${year}`,
        label: formatProductionMonthLabel({
          month,
          year,
          locale: lang || "en",
        }),
      });
      setDate({ month, year });
    }
  };

  useEffect(() => {
    if (productionMonth.isSuccess) {
      if (productionMonth?.data?.length > 0) {
        const sortedData = sortProductionMonthaData(productionMonth.data);
        const currentDate = new Date();

        // Find current production month
        const currentProductionMonth = sortedData.find((data) => {
          const fromDate = parseISO(data?.from_date);
          const toDate = parseISO(data?.to_date);
          return isWithinInterval(currentDate, {
            start: fromDate,
            end: toDate,
          });
        });

        const defaultOption = currentProductionMonth || sortedData[0];
        const optionData = sortedData.map((data) => ({
          value: `${data?.month} ${data?.year}`,
          label: formatProductionMonthLabel({
            month: data?.month,
            year: data?.year,
            locale: lang || "en",
          }),
        }));
        setDate({ month: sortedData?.[0]?.month, year: sortedData?.[0]?.year });
        setDate({ month: defaultOption?.month, year: defaultOption?.year });
        setSelOption({
          value: `${defaultOption?.month} ${defaultOption?.year}`,
          label: formatProductionMonthLabel({
            month: defaultOption?.month,
            year: defaultOption?.year,
            locale: lang || "en",
          }),
        });

        setOptions(optionData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionMonth.isSuccess, productionMonth.data, lang]);

  if (isError) {
    console.log("useNewCollabDashboardQuery error", error);
  } else if (exceliorData.isError) {
    console.log("useNewExCollabDashboardQuery error", exceliorData.error);
  }

  const incentiveDeadline = userData?._date_register
    ? moment().diff(moment(userData._date_register), "days")
    : 0;

  return (
    <div className="dashboard-page">
      <ProfileLoader setHasInvestment={setHasInvestment} />
      <div className="">
        <SetPageTitle title="dashboard" display={false} />
        <Row className="mb-3 justify-space-between align-items-center dashboard-welcome-row">
          <Col>
            <h5 className="text-light mb-0">
              {t("welcome")}, {userData?.first_name} {userData?.last_name}
            </h5>
          </Col>
          {productionMonth.isSuccess && (
            <Select
              options={options}
              styles={selectColourStyles2}
              value={selOption}
              onChange={(e) => handleSetDate(e.value)}
              placeholder={t("sltLabel")}
            />
          )}
        </Row>
        <Row xs={1} sm={2} md={3} xl={5} className="mb-3 dashboard-stat-cards g-3">
          <EwalletCard />
          <SmallCards
            imgSrc={commission}
            title={t("commissionOfMonth")}
            number={
              exceliorData.isLoading ? (
                <SkSmallCard />
              ) : (
                <CurrencyValue amount={exceliorData?.data?.data?.commissionOfMonth} />
              )
            }
            tooltipText={t("totalCommissionsCurrentMonth")}
          />
          <SmallCards
            imgSrc={pool}
            title={t("poolBonus")}
            number={
              isLoading ? (
                <SkSmallCard />
              ) : (
                <CurrencyValue amount={data?.data?.poolBonus} />
              )
            }
            tooltipText={t("totalSilverGoldPoolMonthlyEarnings")}
          />
          <SmallCards
            imgSrc={personal}
            title={t("personalDoc")}
            number={
              isLoading ? (
                <SkSmallCard />
              ) : (
                <CurrencyValue amount={data?.data?.personalDoc} />
              )
            }
            tooltipText={t("totalPersonalDocCurrentMonth")}
          />
          <SmallCards
            imgSrc={network}
            title={t("networkDoc")}
            number={
              isLoading ? (
                <SkSmallCard />
              ) : (
                <CurrencyValue amount={data?.data?.networkDoc} />
              )
            }
            tooltipText={t("totalDocQuotasEntireNetworkCurrentMonth")}
          />
        </Row>
        <Row className="mb-0 mb-lg-3 dashboard-layout-row">
          <Col xs={12} xl={9} className="dashboard-main-col">
            <Row>
              <DashboardChart />
              <IncomeDOC size="6" isLoading={isLoading} data={data} />
              <Col md="6">
                <Row>
                  <Col className="mb-3" md="12">
                    <Card bg="dark1" className="h-100">
                      <Card.Header className="text-light d-flex justify-content-between">
                        <span className="text-uppercase">
                          {t(`nextObjective`)}
                        </span>
                        <span>{t("docMissing")}</span>
                      </Card.Header>
                      <Card.Body>
                        <ul className="list-unstyled">
                          <li className="d-flex justify-content-between mb-3">
                            <span>{t(`teamManager`)}</span>
                            {isLoading ? (
                              <Spinner size="sm" />
                            ) : (
                              <>
                                {`CHF ${formatNumberToLocal(
                                  data?.data?.missingDoc
                                    ?.docMissingForNextPool || 0,
                                )} `}
                                (
                                {
                                  data?.data?.missingDoc
                                    ?.missingDocForTeamManager
                                    ?.nextQualification
                                }
                                )
                              </>
                            )}
                          </li>
                          <li className="d-flex justify-content-between mb-3">
                            <span>{t(`poolBonusSilver`)}</span>
                            {isLoading ? (
                              <Spinner size="sm" />
                            ) : (
                              `CHF ${formatNumberToLocal(data?.data?.missingDoc?.docRequiredForNextPoolLevel || 0)} (${data?.data?.missingDoc?.nextPoolBonusLevel})`
                            )}
                          </li>
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>

                  <ListingCard
                    size="12"
                    title="lastMonthPoolDistribution"
                    isLoading={isLoading}
                    data={[
                      {
                        title: "teamManagerPool",
                        value:
                          data?.data?.distributedPool
                            ?.lastMonthTeamManagerPoolBonus,
                      },
                      {
                        title: "poolBonusSilver",
                        value:
                          data?.data?.distributedPool?.lastMonthSilverPoolBonus,
                      },
                      {
                        title: "poolBonusGold",
                        value:
                          data?.data?.distributedPool?.lastMonthGoldPoolBonus,
                      },
                    ]}
                    isAmount={true}
                  />
                </Row>
              </Col>
            </Row>
            <TeamPerformance isLoading={isLoading} data={data?.data} />
          </Col>
          <Col xs={12} xl={3} className="dashboard-side-col">
            <Row className="mb-2">
              <Col xs={6} sm={4} md={4} xl={6} className="mb-2">
                <SmallCards2
                  title={t("basicSharePack")}
                  number={
                    isLoading ? (
                      <SkSmallCard />
                    ) : (
                      exceliorData?.data?.data?.sharingPack?.basic || 0
                    )
                  }
                />
              </Col>
              <Col xs={6} sm={4} md={4} xl={6} className="mb-2">
                <SmallCards2
                  title={t("bronzeSharePack")}
                  number={
                    isLoading ? (
                      <SkSmallCard />
                    ) : (
                      exceliorData?.data?.data?.sharingPack?.bronze || 0
                    )
                  }
                />
              </Col>
              <Col xs={6} sm={4} md={4} xl={6} className="mb-2">
                <SmallCards2
                  icon={<i className="fas fa-star silver"></i>}
                  title={t("silverSharePack")}
                  number={
                    isLoading ? (
                      <SkSmallCard />
                    ) : (
                      exceliorData?.data?.data?.sharingPack?.silver || 0
                    )
                  }
                />
              </Col>
              <Col xs={6} sm={4} md={4} xl={6} className="mb-1">
                <SmallCards2
                  title={t("goldSharePack")}
                  number={
                    isLoading ? (
                      <SkSmallCard />
                    ) : (
                      exceliorData?.data?.data?.sharingPack?.gold || 0
                    )
                  }
                />
              </Col>
              <Col xs={6} sm={4} md={4} xl={6} className="mb-1">
                <SmallCards2
                  title={"Founder Packs"}
                  number={
                    isLoading ? (
                      <SkSmallCard />
                    ) : (
                      exceliorData?.data?.data?.sharingPack?.founder || 0
                    )
                  }
                />
              </Col>
              <Col xs={6} sm={4} md={4} xl={6} className="mb-1">
                <SmallCards2
                  title={t("packsCommission")}
                  number={
                    isLoading ? (
                      <SkSmallCard />
                    ) : (
                      `CHF ${formatNumberToLocal(exceliorData?.data?.data?.sharingPack?.amount || 0)}`
                    )
                  }
                />
              </Col>
            </Row>
            <Row>
              {/* KYC card */}
              <KycCardDashboard />
              {endRowData.map((section, i) => {
                return (
                  <React.Fragment key={i}>
                    {(() => {
                      if (section.title === "contestStartUp") {
                        return (
                          <>
                            {incentiveDeadline > 90 && (
                              <ListingCard
                                size="12"
                                title={section.title}
                                data={section.items}
                              />
                            )}
                          </>
                        );
                      } else if (section.title === "personalIncentivePlan") {
                        return (
                          <>
                            <ListingCard
                              size="12"
                              title={section.title}
                              isLoading={isLoading}
                              data={[
                                {
                                  title: "incentiveDeadline",
                                  value:
                                    incentiveDeadline < 60
                                      ? `${60 - incentiveDeadline} days`
                                      : "0 days",
                                },
                              ]}
                            />
                          </>
                        );
                      } else {
                        return (
                          <ListingCard
                            size="12"
                            title={section.title}
                            data={section.items}
                            isLoading={isLoading}
                          />
                        );
                      }
                    })()}
                  </React.Fragment>
                );
              })}
              <Col xs={12} md={6} xl={12} className="mb-3">
                <Card bg="dark1" className="h-100">
                  <Card.Header className="text-light text-uppercase">
                    {t("newMembers")}
                  </Card.Header>
                  <Card.Body>
                    {isLoading ? (
                      <div className="text-center py-2">
                        <Spinner size="sm" />
                      </div>
                    ) : data?.data?.newMembers?.length > 0 ? (
                      data?.data?.newMembers.map((team, i) => (
                        <div
                          className="d-flex align-items-center justify-content-between mb-3"
                          key={i}
                        >
                          <div className="d-flex align-items-center">
                            <Image src="/man.png" alt="Man" height={30} />
                            <Link
                              to={`/${lang}/collaborator/${team?.user_id}`}
                              className="ms-3 text-light text-decoration-none"
                            >
                              {team?.user?.first_name} {team?.user?.last_name}
                            </Link>
                          </div>
                          <b>{team?.referral_code}</b>
                        </div>
                      ))
                    ) : (
                      <p className="text-light">{t("noRecordFound")}</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              {(isLoading || data?.data?.teamManager?.user) && (
                <Col xs={12} md={6} xl={12} className="mb-3">
                  <Card bg="dark1" className="h-100">
                    <Card.Header className="text-light text-uppercase">
                      {t("myTeamManager")}
                    </Card.Header>
                    <Card.Body>
                      <Performer
                        isLoading={isLoading}
                        user={{
                          first_name: data?.data?.teamManager?.user?.first_name,
                          last_name: data?.data?.teamManager?.user?.last_name,
                        }}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {(isLoading || data?.data?.closer?.closer) && (
                <Col xs={12} md={6} xl={12} className="mb-3">
                  <Card bg="dark1" className="h-100">
                    <Card.Header className="text-light text-uppercase">
                      {t("myCloser")}
                    </Card.Header>
                    <Card.Body>
                      <Performer
                        isLoading={isLoading}
                        user={{
                          first_name: data?.data?.closer?.closer?.first_name,
                          last_name: data?.data?.closer?.closer?.last_name,
                        }}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CollaboratorDashboard;
